import { getActiveRoleFromToken } from '@moko/utils/src/authHelper';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import { groupWorkActions, type IAvailableWorkAction } from '@moko/utils';
import { statusIcon } from '../StatusBadge';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    flexWrap: 'nowrap',
  },
  action: {
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    flexShrink: 1,
  },
  more: { flexShrink: 0 },
  destructive: { color: tokens.colorPaletteRedForeground1 },
});

export interface WorkActionsProps {
  actions: readonly IAvailableWorkAction[];
  disabled?: boolean;
  onAction: (action: IAvailableWorkAction) => Promise<void>;
}

export default function WorkActions({
  actions,
  disabled,
  onAction,
}: WorkActionsProps) {
  const { t } = useTranslation();
  const styles = useStyles();
  const [confirmation, setConfirmation] = useState<IAvailableWorkAction | null>(
    null
  );
  const visibleActions = actions.filter(
    (action) =>
      !['COMPLETED', 'REPORT_REJECTED'].includes(action.statusCode ?? '') ||
      getActiveRoleFromToken() === 'FMSManager.Access'
  );
  const { primary, secondary, more } = groupWorkActions(visibleActions);
  const execute = (action: IAvailableWorkAction) => {
    if (
      action.statusCode === 'CANCELLED' ||
      action.statusCode === 'REPORT_REJECTED'
    ) {
      setConfirmation(action);
    } else {
      void onAction(action);
    }
  };

  if (visibleActions.length === 0) return null;

  return (
    <div
      className={styles.root}
      role="group"
      aria-label={t('workActions', 'Work actions')}
    >
      {primary && (
        <Button
          className={styles.action}
          appearance="primary"
          disabled={disabled}
          icon={statusIcon(primary.icon ?? undefined, true)}
          onClick={() => execute(primary)}
        >
          {primary.label}
        </Button>
      )}
      {secondary && (
        <Button
          className={mergeClasses(styles.action, styles.destructive)}
          appearance="secondary"
          disabled={disabled}
          icon={statusIcon(secondary.icon ?? undefined, true)}
          onClick={() => execute(secondary)}
        >
          {secondary.label}
        </Button>
      )}
      {more.length > 0 && (
        <Menu>
          <Tooltip
            content={t('moreActions', 'More actions')}
            relationship="label"
          >
            <MenuTrigger disableButtonEnhancement>
              <Button
                className={styles.more}
                appearance="subtle"
                disabled={disabled}
                aria-label={t('moreActions', 'More actions')}
                icon={<MoreHorizontalRegular />}
              />
            </MenuTrigger>
          </Tooltip>
          <MenuPopover>
            <MenuList>
              {more.map((action) => (
                <MenuItem
                  key={action.statusId}
                  disabled={disabled}
                  className={
                    action.statusCode === 'CANCELLED'
                      ? styles.destructive
                      : undefined
                  }
                  icon={statusIcon(action.icon ?? undefined, true)}
                  onClick={() => execute(action)}
                >
                  {action.label}
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
      <Dialog
        open={confirmation != null}
        onOpenChange={(_, data) => {
          if (!data.open) setConfirmation(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{confirmation?.label}</DialogTitle>
            <DialogContent>
              {t('confirmWorkAction', 'Are you sure you want to continue?')}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmation(null)}>
                {t('cancel', 'Cancel')}
              </Button>
              <Button
                appearance="primary"
                disabled={disabled}
                onClick={() => {
                  if (confirmation) void onAction(confirmation);
                  setConfirmation(null);
                }}
              >
                {confirmation?.label}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
